import { ArrowDownTrayIcon as DownloadIcon } from '@heroicons/react/24/outline';
import { default as Form } from 'antd/es/form';
import { default as Input } from 'antd/es/input';
import { default as Modal } from 'antd/es/modal';
import { default as Select } from 'antd/es/select';
import { default as Skeleton } from 'antd/es/skeleton';
import { default as Switch } from 'antd/es/switch';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type { PaymentInstruments } from 'api';
import {
  getDisbursementCapabilitySchemasSchemaIdCsvTemplate,
  useGetDisbursementProductSchemas
} from 'api';

import { useOrganisationId } from 'hooks/context';
import { useCsvDownload } from 'hooks/useCsvDownload';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';

type FormValues = {
  localInstrument: string;
  corridor: string;
  productSchema: string;
  requiredHeadersOnly?: boolean;
  filename?: string;
};

type Props = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  paymentInstrumentsData: PaymentInstruments[];
};

export const CreateBatchTemplateDialog = ({
  open,
  setOpen,
  paymentInstrumentsData
}: Props) => {
  const [form] = Form.useForm();
  const [downloaded, setDownloaded] = useState<boolean>(false);
  const openNotification = useOpenNotification();
  const organisationId = useOrganisationId<number>();
  const { downloadCsv } = useCsvDownload();
  const defaultFilename = 'payout_template.csv';

  // LocalInstruments
  const localInstrumentOptions = useMemo(
    () =>
      paymentInstrumentsData.map((i) => ({
        label: i.local_instrument_family,
        value: i.local_instrument_family
      })),
    [paymentInstrumentsData]
  );

  const localInstrumentCorridorOptionsMap = useMemo(
    () =>
      paymentInstrumentsData.reduce((map, i) => {
        return {
          ...map,
          [i.local_instrument_family]: i.products.map((p) => ({
            label: p.corridor,
            value: p.id.toString()
          }))
        };
      }, {}),
    [paymentInstrumentsData]
  );

  const localInstrument = Form.useWatch('localInstrument', form);

  // Corridors (after localInstruments)
  const corridorOptions = useMemo(
    () => localInstrumentCorridorOptionsMap[localInstrument] ?? [],
    [localInstrument, localInstrumentCorridorOptionsMap]
  );

  const corridor = Form.useWatch('corridor', form);

  // ProductSchemas (after corridors)
  const productId = useMemo(() => {
    return corridor == null || corridor === '' ? undefined : Number(corridor);
  }, [corridor]);

  const { data: productSchemaOptions = [], isFetching: isSchemasPending } =
    useGetDisbursementProductSchemas(
      organisationId,
      { product_id: productId as number },
      {
        query: {
          enabled: Boolean(productId),
          select(res) {
            return (
              res.data?.map((schema) => ({
                value: String(schema.id),
                label: schema.label
              })) ?? []
            );
          }
        }
      }
    );

  useEffect(() => {
    form.resetFields(['corridor', 'productSchema']);
  }, [form, localInstrument]);

  useEffect(() => {
    form.resetFields(['productSchema']);
  }, [corridor, form, productSchemaOptions]);

  const canDownload = Form.useWatch<FormValues>(
    (values) =>
      values.corridor && values.localInstrument && values.productSchema,
    form
  );

  useEffect(() => {
    if (localInstrumentOptions?.length === 1) {
      form.setFieldValue('localInstrument', localInstrumentOptions[0].value);
    }
    if (corridorOptions?.length === 1) {
      form.setFieldValue('corridor', corridorOptions[0].value);
    }
    if (productSchemaOptions?.length === 1) {
      form.setFieldValue('productSchema', productSchemaOptions[0].value);
    }
  }, [
    corridor,
    corridorOptions,
    form,
    localInstrumentOptions,
    productSchemaOptions
  ]);

  const onSubmit = async (values: FormValues) => {
    try {
      const filename = values.filename || defaultFilename;
      const requiredOnly = values.requiredHeadersOnly ?? false;
      const schemaId = Number(values.productSchema);
      const csvString =
        await getDisbursementCapabilitySchemasSchemaIdCsvTemplate(
          organisationId,
          schemaId,
          {
            required_headers_only: requiredOnly
          }
        );

      downloadCsv(csvString, filename);
      setDownloaded(true);
    } catch (_) {
      setDownloaded(false);
      openNotification({ kind: 'error', message: 'CSV download failed' });
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      centered
      className="ant-modal-no-padding"
      destroyOnHidden
      title={
        <div className="flex h-12 items-center gap-2 pl-6 font-normal">
          <DownloadIcon className="size-6 stroke-1 group-hover:stroke-1.5" />
          <span>Create batch template</span>
        </div>
      }
      footer={
        <div className="px-6 pb-6">
          {downloaded === false ? (
            <PrimaryButton
              htmlType="submit"
              size="large"
              className="w-full"
              disabled={!canDownload}
            >
              Download CSV template
            </PrimaryButton>
          ) : (
            <PrimaryButton
              htmlType="submit"
              size="large"
              className="w-full"
              onClick={() => setOpen(false)}
            >
              CSV downloaded, close window
            </PrimaryButton>
          )}
        </div>
      }
      modalRender={(dom) => (
        <Form
          layout="vertical"
          form={form}
          clearOnDestroy
          onFinish={onSubmit}
          onValuesChange={() => setDownloaded(false)}
        >
          {dom}
        </Form>
      )}
    >
      {paymentInstrumentsData.length === 0 ? (
        <Skeleton className="p-6" />
      ) : (
        <div className="overflow-visible px-6">
          <Form.Item name="format" label="Format">
            <Select className="uppercase" disabled={true} defaultValue="CSV" />
          </Form.Item>

          <Form.Item name="localInstrument" label="Transfer type">
            <Select
              disabled={
                !localInstrumentOptions || localInstrumentOptions.length <= 1
              }
              options={localInstrumentOptions}
            />
          </Form.Item>

          <Form.Item name="corridor" label="Corridor">
            <Select
              disabled={!corridorOptions || corridorOptions.length <= 1}
              options={corridorOptions}
            />
          </Form.Item>

          <Form.Item name="productSchema" label="Data required">
            <Select
              disabled={
                !productSchemaOptions || productSchemaOptions.length <= 1
              }
              key={`${corridor}${productSchemaOptions?.length}`}
              options={productSchemaOptions ?? []}
              loading={isSchemasPending}
            />
          </Form.Item>

          <Form.Item
            name="filename"
            label="Template name"
            tooltip="Enter a custom filename, or leave it blank to use the default"
            rules={[
              {
                pattern: /^[\w\-.]+$/,
                message:
                  'Use only letters, numbers, underscores, hyphens, or dots'
              }
            ]}
          >
            <Input style={{ height: 45 }} placeholder={defaultFilename} />
          </Form.Item>

          <Form.Item
            name="requiredHeadersOnly"
            label="Only required headers"
            tooltip="Enable to get a simplified template, or leave it off for the full version"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
        </div>
      )}
    </Modal>
  );
};
